import {Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ObjectClass} from '|api/commons';
import {
  CodebookService,
  DocumentSearchService,
  DocumentsTableColumn,
  DocumentsTableComponent,
  DocumentView,
  getCustomFieldFilterId,
  GLOBAL_SEARCH_FILTER_SCOPE,
  globalSearchAllFilters,
  globalSearchCommonFilters,
  GlobalSearchDataSource,
  globalSearchDocumentOnlyFilters,
  globalSearchFileOnlyFilters,
} from '|shared';
import {MIN_SEARCH_TERM_LENGTH, SearchParams} from '@icz/angular-table';
import {AlertComponent, AutoFocusDirective, LabelComponent} from '@icz/angular-essentials';
import {MessageType, ToastService} from '@icz/angular-application-core';
import {
  ApplicationRoute,
  createAbsoluteRoute,
  DocumentsRoute,
  EsslHistoryService,
  FILTER_PARAM_NAME,
  FULLTEXT_SEARCH_TERM_PARAM_NAME
} from '|ui-essl-core';

export const GLOBAL_SEARCH_PAGE_NAME = 'Vyhledávání';

const GLOBAL_SEARCH_RESULT_COUNT_LIMIT = 10_000;

@Component({
  selector: 'icz-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  imports: [DocumentsTableComponent, LabelComponent, AlertComponent, AutoFocusDirective]
})
export class GlobalSearchComponent implements OnInit {

  private router = inject(Router);
  private codebookService = inject(CodebookService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private activatedRoute = inject(ActivatedRoute);
  private documentSearchService = inject(DocumentSearchService);
  private historyService = inject(EsslHistoryService);
  private destroyRef = inject(DestroyRef);

  GLOBAL_SEARCH_RESULT_COUNT_LIMIT = GLOBAL_SEARCH_RESULT_COUNT_LIMIT;

  dataSource = signal<Nullable<GlobalSearchDataSource>>(null);
  searchTerm = '';

  indexableCustomFieldIds: string[] = [];

  enabledFilters: string[] = [];

  searchTermLengthWarningDisplayed = false;

  tooManyResultsWarningLabel = 'Zobrazujeme maximálně možných {{limit}} vyhovujících výsledků.';

  private lastObjectClasses: ObjectClass[] = [];

  readonly globalSearchTableId = GLOBAL_SEARCH_FILTER_SCOPE;
  readonly DocumentView = DocumentView;

  ngOnInit() {
    this.codebookService.customFields().subscribe(customFields => {
      this.indexableCustomFieldIds = customFields.filter(f => f.indexed).map(getCustomFieldFilterId);

      this.enabledFilters = [
        ...globalSearchAllFilters,
        ...this.indexableCustomFieldIds,
      ];

      this.activatedRoute.queryParams.pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(queryParams => {
        const url = this.router.routerState.snapshot.url;

        if (FULLTEXT_SEARCH_TERM_PARAM_NAME in queryParams) {
          const searchTerm = queryParams[FULLTEXT_SEARCH_TERM_PARAM_NAME];

          if (searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
            this.toastService.dispatchToast({
              type: MessageType.ERROR,
              data: {
                header: {
                  template: 'Pro vyhledávání musíte zadat alespoň dva znaky.',
                }
              }
            });

            this.router.navigateByUrl(ApplicationRoute.ROOT);
          }
          else {
            this.historyService.setHistoryBitAlias({
              url,
              label: this.translateService.instant(GLOBAL_SEARCH_PAGE_NAME) + ': ' + searchTerm,
              labelParts: [`${GLOBAL_SEARCH_PAGE_NAME}:`, searchTerm],
            });
          }
        } else {
          this.searchTerm = ' ';
          this.historyService.setHistoryBitAlias({
            url,
            label: GLOBAL_SEARCH_PAGE_NAME,
          });
        }

        if (!this.dataSource()) {
          this.dataSource.set(new GlobalSearchDataSource(this.documentSearchService));
        }
      });
    });
  }

  pageLoadStarted(searchParams: SearchParams) {
    const searchTerm = (searchParams.fulltextSearchTerm ?? '').trim();

    if (searchTerm.length === 0 || searchTerm.length >= MIN_SEARCH_TERM_LENGTH) {
      const objectClassFilter = searchParams.filter.find(f => f.fieldName === DocumentsTableColumn.OBJECT_CLASS);
      const objectClasses: ObjectClass[] = ((objectClassFilter?.value ?? []) as ObjectClass[]).toSorted();

      if (String(this.lastObjectClasses) !== String(objectClasses)) {
        if (objectClasses?.length) {
          this.enabledFilters = [...globalSearchCommonFilters];

          if (objectClasses.some(oc => oc.includes('DOCUMENT'))) {
            this.enabledFilters.push(...globalSearchDocumentOnlyFilters);
          }
          else if (objectClasses.some(oc => oc.includes('FILE'))) {
            this.enabledFilters.push(...globalSearchFileOnlyFilters);
          }
        }
        else {
          this.enabledFilters = globalSearchAllFilters;
        }

        this.enabledFilters.push(...this.indexableCustomFieldIds);

        this.lastObjectClasses = objectClasses;
        this.dataSource()!.reload(true);
      }
    }
    else {
      if (!this.searchTermLengthWarningDisplayed) {
        this.toastService.dispatchToast({
          type: MessageType.ERROR,
          data: {
            header: {
              template: 'Pro vyhledávání musíte zadat alespoň dva znaky.',
            }
          }
        });

        this.searchTermLengthWarningDisplayed = true;
      }
    }
  }

  editComplexQuery() {
    const queryParamParts = location.search.replace('?', '').split('&');
    const filterQueryParam = queryParamParts.find(qp => qp.startsWith(FILTER_PARAM_NAME));

    if (filterQueryParam) {
      this.router.navigateByUrl(createAbsoluteRoute(ApplicationRoute.DOCUMENTS, DocumentsRoute.ADVANCED_SEARCH) + '?' + filterQueryParam);
    }
  }

}
